import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { ContactPoint, ContactPointKind, formatContactPointLine, formatContactPointValue, getPrimaryContactPoint } from './contactPoints';
import { mutedTextClass, primaryLinkClass } from './themeHelpers';

interface ContactPointsDisplayProps {
  points?: ContactPoint[];
  kind: ContactPointKind;
  compact?: boolean;
}

const ContactPointsDisplay: React.FC<ContactPointsDisplayProps> = ({
  points,
  kind,
  compact = false,
}) => {
  const { colors } = useTheme();
  const displayPoints = (points ?? []).filter((point) => point.kind === kind && point.value?.trim());
  const linkClass = cn('hover:underline', primaryLinkClass(colors));

  if (displayPoints.length === 0) {
    return <span className={mutedTextClass(colors)}>-</span>;
  }

  const primary = getPrimaryContactPoint(displayPoints, kind) ?? displayPoints[0];
  const others = displayPoints.filter((point) => point !== primary);

  if (compact) {
    return (
      <div>
        <div className="text-sm">
          {kind === 'EMAIL' ? (
            <a href={`mailto:${primary.value}`} className={linkClass}>
              {formatContactPointLine(primary)}
            </a>
          ) : (
            <a href={`tel:${primary.value}`} className={linkClass}>
              {formatContactPointLine(primary)}
            </a>
          )}
        </div>
        {others.length > 0 && (
          <div className={cn('text-xs mt-0.5', mutedTextClass(colors))}>+{others.length} more</div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {displayPoints.map((point, index) => {
        const personParts = [point.contactName?.trim(), point.contactPosition?.trim()].filter(Boolean);
        const personLine = personParts.length > 0 ? personParts.join(' · ') : null;
        return (
          <div key={point.id ?? `${point.value}-${index}`} className="text-sm">
            {personLine ? (
              <div className={cn('text-xs font-medium mb-0.5', colors.textSecondary)}>{personLine}</div>
            ) : null}
            {kind === 'EMAIL' ? (
              <a href={`mailto:${point.value}`} className={linkClass}>
                {formatContactPointValue(point)}
                {point.isPrimary ? ' (primary)' : ''}
              </a>
            ) : (
              <a href={`tel:${point.value}`} className={linkClass}>
                {formatContactPointValue(point)}
                {point.isPrimary ? ' (primary)' : ''}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ContactPointsDisplay;
